import { Alert, Box, Button, Card, CircularProgress, Divider, Grid, LinearProgress, Tab, TableBody, Tabs, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import useLazyLoading from "../../common/hooks/loader";
import Loader from "../../components/loader";
import { useNotification } from "../../components/notification/useNotification";
import extractErrorMessage from "../../common/utils/errorMessages";
import ILiquidityPoolDto from "../../interfaces/liquidity-pool/ILiquidityPoolDto";
import { defaultControllerPagination, IPagination } from "../../interfaces/pagination/IPagination";
import { tryGetAllLiquidityPools, trySyncLiquidityPools } from "../../services/liquidity-pool/liquidity-pool.service";
import { IHeaderGrid } from "../../interfaces/grid/IHeaderGrid";
import GridContent from "../../components/grid";
import GridHeader from "../../components/grid/header";
import Pagination from "../../components/grid/pagination";
import PoolSummaryCards from "./components/pool-summary-cards";
import PoolFilters, { PoolFiltersState } from "./components/pool-filters";
import PoolPositionRow from "./components/pool-position-row";
import SyncIcon from "@mui/icons-material/Sync";

const DEFAULT_FILTERS: PoolFiltersState = { network: '', protocol: '', walletId: '' };

const headerColumnList: IHeaderGrid[] = [
    { align: 'left',  name: 'Pool',            padding: 'normal' },
    { align: 'left',  name: 'Posição Atual',   padding: 'normal' },
    { align: 'left',  name: 'Balance (USD)',    padding: 'normal' },
    { align: 'left',  name: 'Taxas Pendentes',  padding: 'normal' },
    { align: 'left',  name: 'Position Range',   padding: 'normal' },
    { align: 'left',  name: 'Preço Atual',      padding: 'normal' },
    { align: 'left',  name: 'Estado',           padding: 'normal' },
];

export default function LiquidityPoolPage() {
    const { displayNotification } = useNotification();
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const showLoading = useLazyLoading(loading);
    const [poolList, setPoolList] = useState<ILiquidityPoolDto[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    const [openCount, setOpenCount] = useState<number>(0);
    const [closedCount, setClosedCount] = useState<number>(0);
    const [pagination, setPagination] = useState<IPagination>(defaultControllerPagination);
    const [tab, setTab] = useState(0);
    const [filters, setFilters] = useState<PoolFiltersState>(DEFAULT_FILTERS);

    useEffect(() => {
        setLoading(true);
        loadPools();
        setLoading(false);
    }, [pagination, tab]);

    async function loadPools() {
        try {
            const showClosed = tab === 1;
            const response = await tryGetAllLiquidityPools(pagination, showClosed);

            if (response?.status === 200) {
                setPoolList(response.data.results);
                setRowCount(response.data.rowCount);
                setOpenCount(response.data.openCount);
                setClosedCount(response.data.closedCount);
            }
        } catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    async function handleSync() {
        setSyncing(true);
        try {
            await trySyncLiquidityPools({});
            displayNotification({ type: 'success', message: 'Sincronização concluída com sucesso.' });
            await loadPools();
        } catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        } finally {
            setSyncing(false);
        }
    }

    function handleTabChange(_: React.SyntheticEvent, newTab: number) {
        setTab(newTab);
        setPagination(defaultControllerPagination);
    }

    function applyFilters(list: ILiquidityPoolDto[]): ILiquidityPoolDto[] {
        return list.filter(p => {
            if (filters.network && String(p.network) !== filters.network) return false;
            if (filters.protocol && p.protocol !== filters.protocol) return false;
            if (filters.walletId && p.walletId !== filters.walletId) return false;
            return true;
        });
    }

    const displayedPools = applyFilters(poolList);

    return (
        <>
            {showLoading === true ? <Loader /> : null}

            {/* Header */}
            <Box mt={3} mb={1}>
                <Grid container justifyContent="space-between" alignItems="flex-start">
                    <Grid>
                        <Typography variant="h4" fontWeight={700} sx={{ color: 'primary.main', letterSpacing: 1 }}>
                            Pools de Liquidez
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            Posições DeFi em pools de liquidez das tuas carteiras
                        </Typography>
                    </Grid>
                    <Grid>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={syncing
                                ? <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
                                : <SyncIcon />
                            }
                            onClick={handleSync}
                            disabled={syncing}
                        >
                            {syncing ? 'A sincronizar...' : 'Sincronizar'}
                        </Button>
                    </Grid>
                </Grid>
                <Divider sx={{ mt: 2, borderColor: 'rgba(255,215,0,0.15)' }} />
                {syncing && (
                    <LinearProgress
                        sx={{
                            mt: 1,
                            borderRadius: 1,
                            '& .MuiLinearProgress-bar': { backgroundColor: 'primary.main' },
                            backgroundColor: 'rgba(255,215,0,0.12)',
                        }}
                    />
                )}
            </Box>

            {syncing && (
                <Alert
                    severity="info"
                    icon={<CircularProgress size={18} thickness={5} sx={{ color: 'info.main', mt: 0.25 }} />}
                    sx={{ mt: 2, borderRadius: 2, border: '1px solid', borderColor: 'info.dark' }}
                >
                    <Typography variant="body2" fontWeight={600}>Sincronização em curso</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        A consultar as pools de liquidez nas blockchains. Este processo pode demorar alguns segundos…
                    </Typography>
                </Alert>
            )}

            {/* Summary cards */}
            <Box mt={3}>
                <PoolSummaryCards pools={poolList} openCount={openCount} closedCount={closedCount} />
            </Box>

            {/* Filters */}
            <Box mt={2.5}>
                <PoolFilters pools={poolList} filters={filters} onChange={setFilters} />
            </Box>

            {/* Tabs */}
            <Box mt={2}>
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    sx={{
                        borderBottom: '1px solid rgba(255,215,0,0.15)',
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
                    }}
                >
                    <Tab label={`Abertas (${openCount})`} />
                    <Tab label={`Encerradas (${closedCount})`} />
                </Tabs>
            </Box>

            {/* Table */}
            <Card sx={{ mt: 2, border: '1px solid rgba(255,215,0,0.15)' }}>
                <GridContent>
                    <GridHeader headerProps={headerColumnList} />
                    <TableBody>
                        {displayedPools.length === 0 ? (
                            <tr>
                                <td colSpan={7}>
                                    <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                            {poolList.length === 0
                                                ? 'Nenhuma posição encontrada. Clica em "Sincronizar" para carregar as pools.'
                                                : 'Nenhuma posição corresponde aos filtros aplicados.'}
                                        </Typography>
                                    </Box>
                                </td>
                            </tr>
                        ) : (
                            displayedPools.map((pool, index) => (
                                <PoolPositionRow key={pool.id} pool={pool} index={index} />
                            ))
                        )}
                    </TableBody>
                </GridContent>
                <Pagination rowCount={rowCount} pagination={pagination} setController={setPagination} />
            </Card>
        </>
    );
}
